const SCRIPT_BASE_URL = 'https://www.google.com/recaptcha/enterprise.js';
const BADGE_SELECTOR = '.grecaptcha-badge';

let badgeObserver = null;

const loadPromises = new Map();

export function loadRecaptchaScript(siteKey) {
  if (!siteKey) {
    return Promise.reject(new Error('reCAPTCHA site key is not configured'));
  }

  const cached = loadPromises.get(siteKey);
  if (cached) {
    return cached;
  }

  const promise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('reCAPTCHA requires a browser environment'));
      return;
    }

    hideBadge();

    function ready() {
      const enterprise = window.grecaptcha && window.grecaptcha.enterprise;
      if (!enterprise || typeof enterprise.ready !== 'function') {
        reject(new Error('reCAPTCHA script loaded but window.grecaptcha.enterprise is unavailable'));
        return;
      }
      enterprise.ready(() => resolve(enterprise));
    }

    if (window.grecaptcha && window.grecaptcha.enterprise) {
      ready();
      return;
    }

    const selector = `script[data-tm-recaptcha="${siteKey}"]`;
    const script = document.querySelector(selector) || createScript(siteKey);

    script.addEventListener('load', ready);
    script.addEventListener('error', () => {
      script.remove();
      reject(new Error('Failed to load reCAPTCHA script'));
    });
  });

  const retryable = promise.catch((error) => {
    loadPromises.delete(siteKey);
    throw error;
  });

  loadPromises.set(siteKey, retryable);
  return retryable;
}

function createScript(siteKey) {
  const script = document.createElement('script');
  script.src = `${SCRIPT_BASE_URL}?render=${encodeURIComponent(siteKey)}`;
  script.async = true;
  script.defer = true;
  script.dataset.tmRecaptcha = siteKey;
  document.head.appendChild(script);
  return script;
}

// Hidden through CSSOM rather than an injected <style>: the badge lives in the
// embedding page's light DOM, and a style element there would require
// style-src 'unsafe-inline' on their CSP. Google's script creates the badge
// asynchronously, so we watch for it.
function hideBadge() {
  const hide = () => {
    const badge = document.querySelector(BADGE_SELECTOR);
    if (badge) badge.style.visibility = 'hidden';
    return Boolean(badge);
  };

  if (hide() || badgeObserver) return;

  badgeObserver = new MutationObserver(() => {
    if (hide()) {
      badgeObserver.disconnect();
      badgeObserver = null;
    }
  });
  badgeObserver.observe(document.body, { childList: true, subtree: true });
}
